import React from 'react';
import { Brain, Database, Search, ArrowRight } from 'lucide-react';
import { useScrollReveal } from '@/lib/useScrollReveal';
import clsx from 'clsx';

const steps = [
  {
    number: '1',
    icon: Brain,
    title: 'Remember',
    description:
      'Agent stores context automatically via SDK. Every task progress, tool result, and user preference is captured without manual intervention.',
  },
  {
    number: '2',
    icon: Database,
    title: 'Store',
    description:
      'Embeddings are generated and memories are stored securely across provider boundaries. Your agent\'s context survives any provider switch.',
  },
  {
    number: '3',
    icon: Search,
    title: 'Recall',
    description:
      'Retrieve relevant memories for current task context. Ask natural language queries and get ranked, relevant context returned instantly.',
  },
];

const codeSnippet = `// Remember context automatically
await memory.remember({
  type: 'task_progress',
  content: 'Completed Phase 2 of database migration'
});

// Recall relevant memories for current task
const memories = await memory.recall({
  query: 'What stage is the database migration in?',
  limit: 5
});`;

export const HowItWorks: React.FC = () => {
  const { ref, isVisible } = useScrollReveal<HTMLDivElement>();

  return (
    <section className="py-20 bg-white" ref={ref}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`text-center mb-16 transition-all duration-500 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <h2 className="text-4xl md:text-5xl font-bold text-text-primary mb-4">
            How It Works
          </h2>
          <p className="text-xl text-text-secondary max-w-2xl mx-auto">
            Three steps to give your agents permanent memory
          </p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {steps.map((step, index) => (
            <div
              key={step.title}
              className={clsx(
                'relative transition-all duration-500',
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
              )}
              style={{ transitionDelay: `${(index + 1) * 150}ms` }}
            >
              {/* Connector line */}
              {index < steps.length - 1 && (
                <div className="hidden md:block absolute top-12 left-[calc(50%+3rem)] w-[calc(100%-6rem)] h-0.5 bg-border z-0">
                  <ArrowRight className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 text-text-muted transform translate-x-1/2" />
                </div>
              )}

              <div className="relative z-10 flex flex-col items-center text-center bg-surface-secondary rounded-xl p-8 h-full">
                <div className="w-14 h-14 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg mb-4">
                  {step.number}
                </div>
                <step.icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-semibold text-text-primary mb-2">{step.title}</h3>
                <p className="text-text-secondary leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Code Snippet */}
        <div
          className={clsx(
            'rounded-xl overflow-hidden transition-all duration-500',
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          )}
          style={{ transitionDelay: '500ms' }}
        >
          <div className="bg-code-bg p-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-text-muted font-mono">memory.ts</span>
              <span className="text-xs text-text-muted">TypeScript</span>
            </div>
            <pre className="text-sm font-mono text-slate-300 overflow-x-auto">
              <code>{codeSnippet}</code>
            </pre>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;